/**
 * Panneau de publication : confidentialité, métadonnées YouTube (pré-remplies
 * via les générateurs purs), programmation, et état de connexion OAuth.
 */

import { Link } from '@tanstack/react-router';
import { cn } from '@jeraisonne/ui';
import { Youtube, CheckCircle2, AlertCircle } from 'lucide-react';
import type { SocialPrivacy } from '@jeraisonne/db/schemas/youtube';
import { PRIVACY_LABEL } from '@/lib/admin/youtube-labels';
import { useSocialAccount } from '@/lib/admin/youtube-queries';
import { inputCls, labelCls } from './ui';

export interface PublishConfig {
  privacy: SocialPrivacy;
  scheduledAt: string; // datetime-local ('' = immédiat)
  ytTitle: string;
  ytDescription: string;
  ytTags: string[];
}

interface PublishPanelProps {
  value: PublishConfig;
  onChange: (next: PublishConfig) => void;
}

export function PublishPanel({ value, onChange }: PublishPanelProps) {
  const { data: account, isLoading } = useSocialAccount('youtube');
  const connected = !!account;
  const set = <K extends keyof PublishConfig>(k: K, v: PublishConfig[K]) =>
    onChange({ ...value, [k]: v });

  return (
    <div className="space-y-4">
      {/* État OAuth */}
      <div
        className={cn(
          'flex items-center gap-3 rounded-xl border p-3',
          connected ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50',
        )}
      >
        <Youtube className={connected ? 'text-emerald-600' : 'text-amber-600'} />
        <div className="min-w-0 flex-1">
          {isLoading ? (
            <span className="text-ink-500 text-sm">Vérification…</span>
          ) : connected ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800">
              <CheckCircle2 className="h-4 w-4" /> Chaîne connectée :{' '}
              {account?.channel_title ?? 'YouTube'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-amber-800">
              <AlertCircle className="h-4 w-4" /> Aucune chaîne connectée
            </span>
          )}
        </div>
        <Link
          to="/tutor/admin/youtube/connect"
          className="border-ink-200 text-ink-700 inline-flex h-9 shrink-0 items-center rounded-lg border bg-white px-3 text-xs font-bold transition-colors active:scale-[.98]"
        >
          {connected ? 'Gérer' : 'Connecter'}
        </Link>
      </div>

      {/* Confidentialité */}
      <div>
        <span className={labelCls}>Confidentialité</span>
        <div className="mt-2 flex gap-2">
          {(['private', 'unlisted', 'public'] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => set('privacy', p)}
              className={cn(
                'h-10 flex-1 rounded-lg border text-xs font-bold transition-colors active:scale-[.98]',
                value.privacy === p
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-ink-200 text-ink-600 bg-white',
              )}
            >
              {PRIVACY_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className={labelCls}>Titre YouTube</span>
        <input
          className={inputCls}
          maxLength={100}
          value={value.ytTitle}
          onChange={(e) => set('ytTitle', e.target.value)}
        />
      </label>

      <label className="block">
        <span className={labelCls}>Description</span>
        <textarea
          className={cn(inputCls, 'min-h-40 resize-y font-mono text-[12px]')}
          maxLength={5000}
          value={value.ytDescription}
          onChange={(e) => set('ytDescription', e.target.value)}
        />
      </label>

      <label className="block">
        <span className={labelCls}>Tags (séparés par des virgules)</span>
        <input
          className={inputCls}
          value={value.ytTags.join(', ')}
          onChange={(e) =>
            set(
              'ytTags',
              e.target.value
                .split(',')
                .map((t) => t.trim())
                .filter(Boolean),
            )
          }
        />
      </label>

      <label className="block">
        <span className={labelCls}>Programmer la publication (optionnel)</span>
        <input
          type="datetime-local"
          className={inputCls}
          value={value.scheduledAt}
          onChange={(e) => set('scheduledAt', e.target.value)}
        />
        <span className="text-ink-400 mt-1 block text-xs">
          Vide = publier dès le rendu terminé.
        </span>
      </label>
    </div>
  );
}
